<template lang="pug">
ol.vv-breadcrumbs.text-subtitle-2.text-overline
  li(
    v-for="(crumb, index) in localeBreadcrumbs"
    :key="index"
  )
    NuxtLink.a(
      v-if="crumb.to"
      :to="crumb.to"
      @click="onClickLinkCanGoOutsideModal"
    )
      span(v-html="crumb.title")
    span(
      v-else
      v-html="crumb.title"
    )
</template>

<script setup lang="ts">
import { useRouter } from "vue-router";
import { computed } from "vue";
import { useModalRouterUtils } from "~/composables/useModalRouterUtils";
import type {
  BreadCrumbItem,
  LocaleBreadCrumbItem,
  Props,
} from "~/components/VvBreadcrumbs.types";
import type { Dict } from "~/types";

const router = useRouter();

const { onClickLinkCanGoOutsideModal } = useModalRouterUtils();

const props = withDefaults(defineProps<Props>(), {
  breadcrumbs: () => [],
});

const localeRoute = useLocaleRoute();

const localeBreadcrumbs = computed<LocaleBreadCrumbItem[]>(() => {
  return props.breadcrumbs.map(
    (crumb: BreadCrumbItem): LocaleBreadCrumbItem => {
      const localeTo = crumb.to ? localeRoute(crumb.to) : undefined;
      return {
        ...crumb,
        to: localeTo,
      };
    },
  );
});

const runtimeConfig = useRuntimeConfig();

const breadCrumbJsonLd = computed(() => {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: localeBreadcrumbs.value.map((crumb) => {
      const listItem: Dict<string | number> = {
        "@type": "ListItem",
        position: localeBreadcrumbs.value.indexOf(crumb) + 1,
        name: crumb.plainTitle ? crumb.plainTitle : crumb.title,
      };

      if (crumb.to) {
        const url = new URL(
          router.resolve(crumb.to).href,
          runtimeConfig.public.site.url,
        );
        listItem.item = url.href;
      }

      return listItem;
    }),
  };
});

useHead({
  script: [
    {
      type: "application/ld+json",
      innerHTML: JSON.stringify(breadCrumbJsonLd.value),
    },
  ],
});
</script>

<style scoped lang="scss">
ol {
  list-style-type: none;
  padding: 0;

  li {
    display: inline-block;

    &:not(:first-child) {
      margin-left: 1ex;
    }

    &::after {
      content: " > ";
    }

    &:last-child::after {
      content: "";
    }
  }

  a {
    display: inline-block;
    text-underline-offset: 0.5em;
    > * {
      text-underline-offset: initial;
    }
  }
}
</style>
